import {
  Disclaimer,
  Div,
  DropdownMenuComponent,
  Icon,
  lightTheme,
  P,
  Radio,
  useOnClickOutside,
  X,
} from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';

import { VDriveProvider } from 'components/src/types/shared.types';
import { renderVDriveIcon, renderVDriveProviderName } from '../../helpers';
import {
  AdminSelectWrapperChildStyled,
  AdminSelectWrapperStyled,
  ChevronStyled,
  ProviderImage,
  SelectedStyled,
  SelectOption,
} from './AdminCustomProviderSelect.styled';
import styled from 'styled-components';
const IconStyledClear = styled.div`
  position: absolute;
  right: 18px;
  left: auto;
  bottom: 9px;
  transform: translate(0, -50%);
  z-index: 1;
  height: 12px;
  cursor: pointer;
`;

export const AdminCustomVDriveProviderSelect = <_, T = null>({
  onChange,
  value,
  label,
  providerArray = VDriveProvider,
  additionalOptions = [],
  emptyPlaceholder = 'No data',
  $textCenter = false,
  placeholder = 'Select Provider',
  $shadowDesign = true,
  disabledProviders = [],
  multiSelect = false,
  $clearable = false,
}: {
  onChange: (
    provider: VDriveProvider | T | null | (VDriveProvider | T)[]
  ) => void;
  value: VDriveProvider | T | null | (VDriveProvider | T)[];
  label?: string;
  providerArray?: Record<string | number, VDriveProvider> | VDriveProvider[];
  menu?: boolean;
  additionalOptions?: T[];
  emptyPlaceholder?: string;
  $textCenter?: boolean;
  placeholder?: string;
  $shadowDesign?: boolean;
  disabledProviders?: (VDriveProvider | T)[];
  multiSelect?: boolean;
  $clearable?: boolean;
}) => {
  const [dropdown, setDropdown] = useState(false);

  const options = Object.values(providerArray).map((p) => p);

  // Ensure disabledProviders is never null to prevent runtime errors
  const safeDisabledProviders = disabledProviders || [];

  // Helper to get current values as array
  const getCurrentValues = (): (VDriveProvider | T)[] => {
    if (multiSelect) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }
    return value ? [value as VDriveProvider | T] : [];
  };

  // Helper to check if item is selected
  const isSelected = (item: VDriveProvider | T): boolean => {
    const currentValues = getCurrentValues();
    return currentValues.includes(item);
  };

  const dropdownRef = useRef(null);
  useOnClickOutside(dropdownRef, () => dropdown && setDropdown(false));

  const handleSelect = (selectedValue: VDriveProvider | T | null) => {
    if (multiSelect) {
      const currentValues = getCurrentValues();
      let newValues: (VDriveProvider | T)[];

      if (selectedValue === null) {
        newValues = [];
      } else if (currentValues.includes(selectedValue)) {
        // Remove if already selected
        newValues = currentValues.filter((v) => v !== selectedValue);
      } else {
        // Add if not selected
        newValues = [...currentValues, selectedValue];
      }

      onChange(newValues);
    } else {
      onChange(selectedValue);
      setDropdown(false);
    }
  };

  useEffect(() => {
    if (!multiSelect) {
      setDropdown(false);
    }
  }, [value, multiSelect]);

  const handleClear = () => {
    onChange(null);
  };

  const RenderSelectedProviders = () => {
    const currentValues = getCurrentValues();

    if (currentValues.length === 0) {
      return placeholder;
    }

    if (multiSelect) {
      return (
        <>
          <Div $desktop $flex $wrap $gap="12px" $gapSM="8px" $alignCenter>
            {currentValues.slice(0, 2).map((item, index) => (
              <Div key={index} $flex $alignCenter $gap="8px" $gapSM="4px">
                <ProviderImage image={renderVDriveIcon(item as VDriveProvider)}>
                  <img src={renderVDriveIcon(item as VDriveProvider)} />
                </ProviderImage>
                <>
                  {renderVDriveProviderName(item as VDriveProvider)}
                  {index !== currentValues.length - 1 && ','}
                </>
              </Div>
            ))}
            {currentValues.length > 2 && (
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                +{currentValues.length - 2} more
              </Disclaimer>
            )}
          </Div>
          <Div $mobile $flex $wrap $gap="12px" $gapSM="8px" $alignCenter>
            {currentValues.slice(0, 1).map((item, index) => (
              <Div key={index} $flex $alignCenter $gap="8px" $gapSM="4px">
                <ProviderImage image={renderVDriveIcon(item as VDriveProvider)}>
                  <img src={renderVDriveIcon(item as VDriveProvider)} />
                </ProviderImage>
                <>
                  {renderVDriveProviderName(item as VDriveProvider)}
                  {index !== currentValues.length - 1 && ','}
                </>
              </Div>
            ))}
            {currentValues.length > 1 && (
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                +{currentValues.length - 1} more
              </Disclaimer>
            )}
          </Div>
        </>
      );
    } else {
      // Single select display logic
      const singleValue = currentValues[0];
      return (
        <Div $flex $alignCenter $gap="8px" $gapSM="4px">
          <ProviderImage
            image={renderVDriveIcon(singleValue as VDriveProvider)}
          >
            <img src={renderVDriveIcon(singleValue as VDriveProvider)} />
          </ProviderImage>
          {renderVDriveProviderName(singleValue as VDriveProvider)}
        </Div>
      );
    }
  };

  return (
    <Div $relative>
      {label && (
        <P $mb="8" $ml="24" $mr="24" $center={$textCenter}>
          <b>{label}</b>
        </P>
      )}

      <AdminSelectWrapperStyled
        $opened={dropdown}
        onClick={() => setDropdown(true)}
        ref={dropdownRef}
        $shadowDesign={$shadowDesign}
      >
        <AdminSelectWrapperChildStyled>
          <Div $flex $center={$textCenter} $width="100%">
            <SelectedStyled>
              <RenderSelectedProviders />
            </SelectedStyled>
          </Div>

          {(!$clearable || value === null) && (
            <ChevronStyled>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.29365 12.7066C7.68428 13.0973 8.31865 13.0973 8.70928 12.7066L14.7093 6.70664C15.0999 6.31602 15.0999 5.68164 14.7093 5.29102C14.3187 4.90039 13.6843 4.90039 13.2937 5.29102L7.9999 10.5848L2.70615 5.29414C2.31553 4.90352 1.68115 4.90352 1.29053 5.29414C0.899902 5.68477 0.899902 6.31914 1.29053 6.70977L7.29053 12.7098L7.29365 12.7066Z"
                  fill="#4C4C4C"
                />
              </svg>
            </ChevronStyled>
          )}
        </AdminSelectWrapperChildStyled>

        <DropdownMenuComponent isOpen={dropdown} maxHeight={'270px'} fullWidth>
          <Div $flex $column $gap="8px">
            {options
              .filter((v) => !safeDisabledProviders.includes(v))
              .map((v, i) => (
                <SelectOption
                  selected={isSelected(v)}
                  onClick={() => handleSelect(v)}
                  key={i}
                >
                  <Div $flex $alignCenter $gap="12px">
                    <ProviderImage image={renderVDriveIcon(v)}>
                      <img src={renderVDriveIcon(v)} />
                    </ProviderImage>
                    {renderVDriveProviderName(v)}
                  </Div>
                  {multiSelect && <Radio checked={isSelected(v)} />}
                </SelectOption>
              ))}
            {additionalOptions.map((v, i) => (
              <SelectOption
                selected={isSelected(v)}
                onClick={() => handleSelect(v)}
                key={i}
              >
                <Div $height="28px" $ml="20" $flex $center>
                  {v as string}
                </Div>
                {multiSelect && <Radio checked={isSelected(v)} />}
              </SelectOption>
            ))}
          </Div>

          {options.filter((v) => !safeDisabledProviders.includes(v)).length <
            1 && <Disclaimer $m="0">{emptyPlaceholder}</Disclaimer>}
        </DropdownMenuComponent>
      </AdminSelectWrapperStyled>

      {value !== null && $clearable && (
        <IconStyledClear
          onClick={(e) => {
            e.stopPropagation();
            handleClear();
          }}
        >
          <Icon $pointer $size="12">
            <X fill={lightTheme.darkGray} />
          </Icon>
        </IconStyledClear>
      )}
    </Div>
  );
};
